package com.yendo.mx.repository

import dev.gitlive.firebase.Firebase
import dev.gitlive.firebase.firestore.firestore
import dev.gitlive.firebase.firestore.where
import dev.gitlive.firebase.firestore.FieldPath
import dev.gitlive.firebase.functions.functions
import com.yendo.mx.models.FiltroHome
import com.yendo.mx.models.SeccionHome
import com.yendo.mx.models.RestauranteFirebase
import com.yendo.mx.models.Producto
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.map
import kotlinx.coroutines.flow.catch
import kotlinx.coroutines.flow.flow
import kotlinx.coroutines.flow.flowOf
import com.yendo.mx.utils.getAhora
import kotlinx.serialization.Serializable

@Serializable
data class NegocioBffResponse(
    val id: String = "",
    val nombre: String = "Negocio",
    val logotipo: String = "",
    val header: String = "",
    val tipoNegocio: String = "RESTAURANTE",
    val categoria: String = "General",
    val subcategorias: List<String> = emptyList(),
    val categoriasMenu: List<String> = emptyList(),
    val estaAbierto: Boolean = true,
    val permiteEnvio: Boolean = true,
    val permiteRecoger: Boolean = true,
    val tieneFlotaPropia: Boolean = false,
    val esFavorito: Boolean = false,
    val distanciaKm: Double = 0.0,
    val montoMinimo: Double = 0.0,
    val horaAbre: String = "12:00",
    val horaCierra: String = "22:00"
)

@Serializable
data class CatalogoBffResponse(
    val success: Boolean = false,
    val idZona: String = "arriaga",
    val deliveryZonaAbierto: Boolean = true,
    val secciones: List<SeccionHome> = emptyList(),
    val negocios: List<NegocioBffResponse> = emptyList()
)

data class RespuestaCatalogoCloud(
    val secciones: List<SeccionHome> = emptyList(),
    val negocios: List<RestauranteFirebase> = emptyList()
)

object HomeRepository {
    // 🚀 Usamos el db de GitLive
    private val db = Firebase.firestore

    // 🚀 CACHÉ EN MEMORIA SWR (STALE-WHILE-REVALIDATE) PARA RENDERIZADO EN 0ms AL CAMBIAR DE TAB
    private val seccionesHomeCache = mutableMapOf<String, List<SeccionHome>>()
    private val seccionesVistaCache = mutableMapOf<String, List<SeccionHome>>()

    fun observarSeccionesHome(idZonaActual: String): Flow<List<SeccionHome>> {
        val cached = seccionesHomeCache[idZonaActual]
        return flow {
            if (cached != null) {
                emit(cached)
            }
            db.collection("HomeEstructura")
                .where { "activo" equalTo true }
                .snapshots
                .map { querySnapshot ->
                    querySnapshot.documents.mapNotNull { doc ->
                        try {
                            doc.data<SeccionHome>().copy(id = doc.id)
                        } catch (e: Exception) {
                            null
                        }
                    }.filter { seccion ->
                        val zonaValida = seccion.zonasValidas.contains("TODAS") || seccion.zonasValidas.contains(idZonaActual)
                        val vistaValida = seccion.vistaDestino.isBlank() || seccion.vistaDestino.equals("HOME", ignoreCase = true)
                        zonaValida && vistaValida
                    }.sortedBy { it.orden }
                }
                .collect { secciones ->
                    seccionesHomeCache[idZonaActual] = secciones
                    emit(secciones)
                }
        }.catch { emit(cached ?: emptyList()) }
    }

    // 🚀 OBSERVAR BANNERS Y BLOQUES DE CUALQUIER VISTA ("COMIDA", "COMERCIOS") CON CACHÉ SWR
    fun observarSeccionesPorVista(idZonaActual: String, vistaTarget: String): Flow<List<SeccionHome>> {
        val clave = "${idZonaActual}_$vistaTarget"
        val cached = seccionesVistaCache[clave]

        return flow {
            if (cached != null) {
                emit(cached)
            }
            db.collection("HomeEstructura")
                .where { "activo" equalTo true }
                .snapshots
                .map { querySnapshot ->
                    querySnapshot.documents.mapNotNull { doc ->
                        try {
                            doc.data<SeccionHome>().copy(id = doc.id)
                        } catch (e: Exception) {
                            null
                        }
                    }.filter { seccion ->
                        val zonaValida = seccion.zonasValidas.contains("TODAS") || seccion.zonasValidas.contains(idZonaActual)
                        val vistaValida = seccion.vistaDestino.equals(vistaTarget, ignoreCase = true)
                        zonaValida && vistaValida
                    }.sortedBy { it.orden }
                }
                .collect { secciones ->
                    seccionesVistaCache[clave] = secciones
                    emit(secciones)
                }
        }.catch { emit(cached ?: emptyList()) }
    }

    fun observarFiltrosHome(idZonaActual: String): Flow<List<FiltroHome>> {
        return db.collection("FiltrosHome")
            .where { "activo" equalTo true }
            .snapshots
            .map { querySnapshot ->
                querySnapshot.documents.mapNotNull { doc ->
                    try {
                        doc.data<FiltroHome>().copy(id = doc.id)
                    } catch (e: Exception) {
                        null
                    }
                }.filter { filtro ->
                    filtro.zonasValidas.contains("TODAS") || filtro.zonasValidas.contains(idZonaActual)
                }.sortedBy { it.orden }
            }
            .catch { emit(emptyList()) }
    }

    fun obtenerNegociosParaSeccion(seccion: SeccionHome, idZonaActual: String, limite: Int = 15): Flow<List<RestauranteFirebase>> {
        println("🔍 [HOME] Cargando negocios para sección: '${seccion.titulo}' (Tipo: ${seccion.tipoBloque})")

        // 🚀 Construimos la consulta estilo GitLive
        val query = if (seccion.idsManuales.isNotEmpty()) {
            println("🔍 [HOME] Usando IDS manuales: ${seccion.idsManuales}")
            // 🚀 Usamos FieldPath.documentId para filtrar por los IDs de los documentos
            db.collection("Negocios").where {
                all(
                    "ubicacion.id_zona" equalTo idZonaActual,
                    "operacion.visible" equalTo true,
                    "operacion.activo_comercio" equalTo true,
                    "operacion.bloqueado_por_admin" equalTo false, // 🚀 MANDO ADMIN
                    FieldPath.documentId inArray seccion.idsManuales
                )
            }
        } else if (seccion.etiquetaBusqueda.isNotEmpty()) {
            println("🔍 [HOME] Usando etiqueta: ${seccion.etiquetaBusqueda}")
            val q = db.collection("Negocios").where {
                all(
                    "ubicacion.id_zona" equalTo idZonaActual,
                    "operacion.visible" equalTo true,
                    "operacion.activo_comercio" equalTo true,
                    "operacion.bloqueado_por_admin" equalTo false, // 🚀 MANDO ADMIN
                    "etiquetasGlobales" contains seccion.etiquetaBusqueda
                )
            }
            if (limite > 0) q.limit(limite) else q
        } else {
            println("⚠️ [HOME] Sección '${seccion.titulo}' no tiene IDS ni etiqueta")
            return flowOf(emptyList()) // Si no hay reglas, no buscamos nada
        }

        return query.snapshots
            .map { qs ->
                println("🔍 [HOME] snapshot recibido para '${seccion.titulo}'. Documentos: ${qs.documents.size}")
                val listaMapeada = qs.documents.mapNotNull { doc ->
                    try {
                        var rest = doc.data<RestauranteFirebase>().copy(id = doc.id)
                        
                        if (rest.ubicacion.idZona.isBlank()) {
                            rest = rest.copy(ubicacion = rest.ubicacion.copy(idZona = idZonaActual))
                        }
                        
                        val ahora = getAhora()
                        val estaPausado = rest.operacion.pausadoManualmente && rest.operacion.pausaHasta > ahora
                        
                        if (estaPausado) {
                            null
                        } else {
                            rest
                        }
                    } catch (e: Exception) {
                        println("🚨 ERROR MAPEANDO RESTAURANTE (${doc.id}): ${e.message}")
                        null
                    }
                }

                // 🚀 ORDENACIÓN Y LÍMITE (SI LIMITE > 0 APLICA TAKE, SI ES 0 MUESTRA TODOS PARA "VER TODO")
                val ordenada = if (seccion.idsManuales.isNotEmpty()) {
                    listaMapeada.sortedBy { rest ->
                        val index = seccion.idsManuales.indexOf(rest.id)
                        if (index != -1) index else Int.MAX_VALUE
                    }
                } else {
                    listaMapeada.sortedBy { rest ->
                        val ahora = getAhora()
                        val estaPausado = rest.operacion.pausadoManualmente && rest.operacion.pausaHasta > ahora
                        val estaAbierto = rest.operacion.estado == "ACTIVO" && !estaPausado
                        if (estaAbierto) 0 else 1
                    }
                }

                if (limite > 0) ordenada.take(limite) else ordenada
            }
            .catch { 
                println("🚨 ERROR CARGANDO CARRUSEL: ${it.message}") 
                emit(emptyList())
            }
    }

    // =================================================================
    // 🚀 OBTENER PRODUCTOS (CARRUSEL CUADRADO)
    // =================================================================
    fun obtenerProductosParaSeccion(seccion: SeccionHome, idZonaActual: String, limite: Int = 15): Flow<List<Producto>> {
        println("🔍 [HOME] Cargando PRODUCTOS para sección: '${seccion.titulo}' (Tipo: ${seccion.tipoBloque})")

        val query = if (seccion.idsManuales.isNotEmpty()) {
            println("🔍 [HOME] Usando IDS manuales de productos: ${seccion.idsManuales}")
            db.collection("Productos").where {
                all(
                    "id_zona" equalTo idZonaActual,
                    FieldPath.documentId inArray seccion.idsManuales
                )
            }
        } else if (seccion.etiquetaBusqueda.isNotEmpty()) {
            println("🔍 [HOME] Usando etiqueta de productos: ${seccion.etiquetaBusqueda}")
            val q = db.collection("Productos").where {
                all(
                    "id_zona" equalTo idZonaActual,
                    "etiquetas" contains seccion.etiquetaBusqueda
                )
            }
            if (limite > 0) q.limit(limite) else q
        } else {
            println("⚠️ [HOME] Sección de productos '${seccion.titulo}' no tiene IDS ni etiqueta")
            return flowOf(emptyList())
        }

        return query.snapshots
            .map { qs ->
                println("🔍 [HOME] Snapshot recibido de PRODUCTOS. Documentos: ${qs.documents.size}")
                val listaMapeada = qs.documents.mapNotNull { doc ->
                    try {
                        val prod = doc.data<Producto>()
                        val productoFinal = prod.copy(id = doc.id)
                        productoFinal
                    } catch (e: Exception) {
                        println("🚨 ERROR MAPEANDO PRODUCTO (${doc.id}): ${e.message}")
                        null
                    }
                }

                // 🚀 ORDENACIÓN Y LÍMITE (SI LIMITE > 0 APLICA TAKE, SI ES 0 MUESTRA TODOS PARA "VER TODO")
                val ordenada = if (seccion.idsManuales.isNotEmpty()) {
                    listaMapeada.sortedBy { prod ->
                        val index = seccion.idsManuales.indexOf(prod.id)
                        if (index != -1) index else Int.MAX_VALUE
                    }
                } else {
                    listaMapeada.sortedWith(
                        compareByDescending<Producto> { it.operacion.disponible }
                            .thenByDescending { it.destacado }
                    )
                }

                if (limite > 0) ordenada.take(limite) else ordenada
            }
            .catch { 
                println("🚨 ERROR CARGANDO CARRUSEL DE PRODUCTOS: ${it.message}") 
                emit(emptyList())
            }
    }

    // =================================================================
    // 🚀 OBTENER TIENDITA YENDO LOCAL (LA MAGIA DE LA UBICACIÓN)
    // =================================================================
    fun observarTienditaLocal(idZonaActual: String): Flow<RestauranteFirebase?> {
        println("🔍 [HOME] Buscando Tiendita Yendo Mx en la zona: $idZonaActual")
        return db.collection("Negocios").where {
            all(
                "ubicacion.id_zona" equalTo idZonaActual,
                "operacion.visible" equalTo true,
                "operacion.activo_comercio" equalTo true,
                "operacion.bloqueado_por_admin" equalTo false, // 🚀 MANDO ADMIN
                "info_publica.tipo_negocio" equalTo "TIENDITA" // 🚀 Usamos la variable maestra
            )
        }.snapshots.map { qs ->
            val doc = qs.documents.firstOrNull { doc ->
                val rest = doc.data<RestauranteFirebase>()
                val ahora = getAhora()
                val estaPausado = rest.operacion.pausadoManualmente && rest.operacion.pausaHasta > ahora
                !estaPausado
            }
            if (doc != null) {
                println("✅ [HOME] Tiendita Yendo Mx encontrada: ${doc.id}")
                var rest = doc.data<RestauranteFirebase>().copy(id = doc.id)
                // 🚀 ESCUDO: Inyectamos la zona si viene vacía
                if (rest.ubicacion.idZona.isBlank()) {
                    rest = rest.copy(ubicacion = rest.ubicacion.copy(idZona = idZonaActual))
                }
                rest
            } else {
                println("⚠️ [HOME] No hay Tiendita Yendo Mx en esta zona.")
                null
            }
        }.catch {
            println("🚨 ERROR CARGANDO TIENDITA LOCAL: ${it.message}")
            // En Kotlin Flow usamos emit(null) cuando estamos dentro de un bloque catch en lugar de flowOf
            emit(null)
        }
    }

    // 🚀 CACHÉ EN MEMORIA SWR PARA CATÁLOGOS CLOUD (0ms al regresar al Home)
    private val cacheCatalogoCloud = mutableMapOf<String, RespuestaCatalogoCloud>()

    fun obtenerCacheCatalogo(idZona: String, vistaTarget: String): RespuestaCatalogoCloud {
        val clave = "${idZona}_$vistaTarget"
        return cacheCatalogoCloud[clave] ?: RespuestaCatalogoCloud()
    }

    /**
     * 🚀 BACKEND BFF: CONSUMO DE CATÁLOGO HOME PRE-CALCULADO Y ENRIQUECIDO EN LA NUBE
     * Ejecuta en Google Cloud en 0ms sin procesar reglas locales en la CPU del teléfono.
     */
    suspend fun obtenerCatalogoCompletoCloud(
        idZona: String,
        lat: Double = 0.0,
        lng: Double = 0.0,
        vistaTarget: String = "HOME"
    ): RespuestaCatalogoCloud {
        val clave = "${idZona}_$vistaTarget"
        val cached = cacheCatalogoCloud[clave]

        return try {
            println("YENDO_LOG BFF: Solicitando catálogo Cloud para Zona '$idZona', Vista '$vistaTarget'")
            val callable = Firebase.functions.httpsCallable("obtenerCatalogoHome")
            val params = mapOf(
                "idZona" to idZona,
                "lat" to lat,
                "lng" to lng,
                "vistaTarget" to vistaTarget
            )
            val result = callable.invoke(params)
            val response = result.data<CatalogoBffResponse>()
            println("YENDO_LOG BFF: Respuesta Cloud recibida -> ${response.negocios.size} negocios, ${response.secciones.size} secciones/carruseles cargados")

            val listaNegocios = response.negocios.map { item ->
                val franjaGenerada = listOf(com.yendo.mx.models.FranjaHoraria(activo = true, abre = item.horaAbre, cierra = item.horaCierra))
                val horariosInyectados = com.yendo.mx.models.Horarios(
                    lunes = franjaGenerada,
                    martes = franjaGenerada,
                    miercoles = franjaGenerada,
                    jueves = franjaGenerada,
                    viernes = franjaGenerada,
                    sabado = franjaGenerada,
                    domingo = franjaGenerada
                )

                RestauranteFirebase(
                    id = item.id,
                    estaAbiertoCloud = item.estaAbierto,
                    categoriasMenu = item.categoriasMenu,
                    horarios = horariosInyectados,
                    infoPublica = com.yendo.mx.models.InfoPublica(
                        nombre = item.nombre,
                        logotipo = item.logotipo,
                        header = item.header,
                        tipoNegocio = item.tipoNegocio,
                        categoria = item.categoria,
                        subcategorias = item.subcategorias
                    ),
                    ubicacion = com.yendo.mx.models.Ubicacion(
                        idZona = idZona
                    ),
                    operacion = com.yendo.mx.models.Operacion(
                        visible = true,
                        activoComercio = true,
                        deliveryActivo = item.permiteEnvio,
                        deliveryPropioActivo = item.tieneFlotaPropia,
                        pickupActivo = item.permiteRecoger
                    )
                )
            }

            val resultadoFinal = RespuestaCatalogoCloud(
                secciones = response.secciones,
                negocios = listaNegocios
            )
            cacheCatalogoCloud[clave] = resultadoFinal
            resultadoFinal
        } catch (e: Exception) {
            println("YENDO_LOG ERROR BFF CATALOGO CLOUD: ${e.message}")
            cached ?: RespuestaCatalogoCloud()
        }
    }
}